import React from 'react'
import { Link } from 'react-router-dom'
import Chart from '../../components/chart/chart'
import EnterPredictions from './enterPredictions'
import TopPredictions from "../../components/reports/topPredictions";
import HomeImg from '../../assets/images/New_Hands.png';
import AnnouncementStrategiesImg from '../../assets/images/announcement_2026-02-22/01_trading_strategies_backtesting.jpg';
import AnnouncementMarketImg from '../../assets/images/announcement_2026-02-22/02_market_analysis_general_ticker.jpg';
import AnnouncementBrokerImg from '../../assets/images/announcement_2026-02-22/03_broker_portfolio_holdings.jpg';
import AnnouncementDataSearchImg from '../../assets/images/announcement_2026-02-22/data_search.jpg';
import AnnouncementDataSearchGainLossImg from '../../assets/images/announcement_2026-02-22/04_data_search_predicted_gain_loss.jpg';
import AnnouncementCsvImg from '../../assets/images/announcement_2026-02-22/08_csv_export.jpg';
import AnnouncementBuyHoldImg from '../../assets/images/announcement_2026-02-22/09_buy_hold_comparison.jpg';
import AnnouncementStocksDashboardImg from '../../assets/images/announcement_2026-02-22/stocks_dashboard.jpg';
import AnnouncementStockDetailsImg from '../../assets/images/announcement_2026-02-22/stock_details.jpg';

const FEATURE_CARDS = [
  {
    title: "Multi-Horizon AI Predictions",
    body: "Forecasts from Daily through Yearly horizons across 250+ tracked stocks.",
  },
  {
    title: "Trading Strategies",
    body: "Build rule-based strategies with sentiment filters and backtesting.",
  },
  {
    title: "Data Search Workspace",
    body: "Server-side filtering, sorting, pagination, and custom column views for fast scanning.",
  },
  {
    title: "Backtesting Engine",
    body: "Simulate trades, review win-rate, and compare Portfolio Growth vs Buy & Hold.",
  },
  {
    title: "Market Analysis",
    body: "Read the General Market Brief instantly plus ticker-specific sentiment and subject.",
  },
  {
    title: "Connect Your Broker Portfolio",
    body: "Connect your broker portfolio (read-only), map holdings, and overlay DeepTrading predictions.",
  },
  {
    title: "Stocks Dashboard",
    body: "Visual ticker cards with chart thumbnails and live prediction context.",
  },
  {
    title: "REST API Access (Add-on)",
    body: "Programmatic access with secure per-user API keys and credit-based usage for predictions, top movers, briefs, and market endpoints.",
  },
  {
    title: "CSV Export",
    body: "Export Data Search results and backtest trades to CSV for offline analysis.",
  },
  {
    title: "Accuracy Improvements",
    body: "Hybrid accuracy display blends historical and validation metrics for clearer trust signals.",
  },
  {
    title: "Modern UX Controls",
    body: "Resizable panels, practical table controls, and improved readability across dashboard pages.",
  },
  {
    title: "Daily Strategy Action Email",
    body: "Opt in once to receive a daily Buy/Sell/No Trade email for your active strategy.",
  },
];

const ANNOUNCEMENT_GALLERY = [
  { title: "Trading Strategies + Backtesting", image: AnnouncementStrategiesImg },
  { title: "Market Analysis", image: AnnouncementMarketImg },
  { title: "Broker Portfolio", image: AnnouncementBrokerImg },
  { title: "Data Search", image: AnnouncementDataSearchImg },
  { title: "Data Search Predicted Gain/Loss", image: AnnouncementDataSearchGainLossImg },
  { title: "CSV Export", image: AnnouncementCsvImg },
  { title: "Stocks Dashboard", image: AnnouncementStocksDashboardImg },
  { title: "Stock Prediction Details", image: AnnouncementStockDetailsImg },
  { title: "Buy & Hold Comparison", image: AnnouncementBuyHoldImg },
];

const Home = () => {
  const [activeReleaseShot, setActiveReleaseShot] = React.useState(null);

  const closeReleaseShot = React.useCallback(() => {
    setActiveReleaseShot(null);
  }, []);

  React.useEffect(() => {
    if (!activeReleaseShot) {
      return undefined;
    }

    const onEscape = (event) => {
      if (event.key === 'Escape') {
        closeReleaseShot();
      }
    };

    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('keydown', onEscape);
    };
  }, [activeReleaseShot, closeReleaseShot]);

  React.useEffect(() => {
    if (!activeReleaseShot) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activeReleaseShot]);

  return (
  <div className="bodySec">
    <div className="container">
      <div className="row">
        <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12">
          <div className="homeTxt">
            <Chart ticker="DJI"/>
            <TopPredictions/>
            <EnterPredictions/>
          </div>
        </div>
      </div>

      <div className="row homeInvest">
        <div className="col-12 col-sm-12 col-md-7 col-lg-7 col-xl-7">
          <h4>
            <span>AI Forecasts, Made Practical</span>
          </h4>
          <p>
            DeepTrading.ai is an AI research platform that predicts stock prices across multiple horizons, from the
            next trading day to a year out, using advanced AI Deep Learning models designed for market time-series.
          </p>
          <p>
            Start with the free Deep Index forecast on this page, then explore predictions, track record, and
            accuracy metrics for 250 stocks inside the dashboard.
          </p>
          <Link to="/pricing-plans" className="defaultBtn">
            See pricing plans
          </Link>
        </div>

        <div className="col-12 col-sm-12 col-md-5 col-lg-5 col-xl-5">
          <img className="homeHeroImg" src={HomeImg} alt=""/>
        </div>
      </div>

      <div className="row homeFeatures">
        <div className="col-12">
          <div className="homeFeaturesWrap">
            <div className="homeFeaturesHeader">
              <h4>
                <span>All Features in One Place</span>
              </h4>
              <p>
                DeepTrading.ai combines AI forecasting, strategy tools, and market intelligence into one workflow
                designed to help you scan opportunities, backtest ideas, and act with confidence.
              </p>
            </div>

            <div className="homeFeaturesGrid">
              {FEATURE_CARDS.map((feature) => (
                <article key={feature.title} className="homeFeatureCard">
                  <h5>{feature.title}</h5>
                  <p>{feature.body}</p>
                </article>
              ))}
            </div>

            <div className="homeFeaturesActions">
              <Link to="/pricing-plans" className="defaultBtn">
                Start 7-day free trial
              </Link>
              <Link to="/pricing-plans" className="defaultBtn defaultBtnSecondary">
                View Pricing
              </Link>
            </div>
            <p className="homeFeaturesTrustLine">Cancel anytime. DeepTrading.ai does not place trades.</p>

            <div className="homeReleaseGallery">
              <h5>Release Screenshots (click to enlarge)</h5>
              <p className="homeReleaseGalleryHelper">Tap any screenshot to view a larger version.</p>
              <div className="homeReleaseGalleryGrid">
                {ANNOUNCEMENT_GALLERY.map((item) => (
                  <figure key={item.title} className="homeReleaseShot">
                    <button
                      type="button"
                      className="homeReleaseShotLink"
                      title={`Open ${item.title}`}
                      onClick={() => setActiveReleaseShot(item)}
                    >
                      <img src={item.image} alt={item.title} />
                    </button>
                    <figcaption>{item.title}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {activeReleaseShot && (
        <div className="homeReleaseLightbox" onClick={closeReleaseShot}>
          <div
            className="homeReleaseLightboxPanel"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="homeReleaseLightboxClose"
              aria-label="Close screenshot preview"
              onClick={closeReleaseShot}
            >
              x
            </button>
            <img
              src={activeReleaseShot.image}
              alt={activeReleaseShot.title}
            />
            <div className="homeReleaseLightboxCaption">{activeReleaseShot.title}</div>
          </div>
        </div>
      )}
    </div>
  </div>
);
};

export default Home;
